/** How the canvas writes time: "Tuesday 11 September", "11:30", "48 min", "9 days". */
const LOCALE = 'en-GB';

export const clock = (iso: string): string =>
  new Intl.DateTimeFormat(LOCALE, { hour: '2-digit', minute: '2-digit', hour12: false }).format(
    new Date(iso),
  );

export const dayKey = (iso: string): string => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export function dayLabel(iso: string, now = new Date()): string {
  const d = new Date(iso);
  const long = new Intl.DateTimeFormat(LOCALE, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(d);
  const today = dayKey(now.toISOString());
  const key = dayKey(iso);
  const tomorrow = dayKey(new Date(now.getTime() + 86_400_000).toISOString());
  const yesterday = dayKey(new Date(now.getTime() - 86_400_000).toISOString());
  if (key === today) return `Today · ${long}`;
  if (key === tomorrow) return `Tomorrow · ${long}`;
  if (key === yesterday) return `Yesterday · ${long}`;
  return long;
}

export const dateLabel = (iso: string): string =>
  new Intl.DateTimeFormat(LOCALE, { weekday: 'short', day: 'numeric', month: 'short' }).format(
    new Date(iso),
  );

export function duration(seconds: number): string {
  const m = Math.round(seconds / 60);
  if (m < 1) return 'under a minute';
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest ? `${h}h ${String(rest).padStart(2, '0')}m` : `${h}h`;
}

export function between(startIso: string, endIso: string | null): number {
  return Math.max(0, (Date.parse(endIso ?? startIso) - Date.parse(startIso)) / 1000);
}

/** "9 days", "3 hours", "just now" — an age, always as a fact. */
/**
 * How long ago, as a phrase that can stand on its own.
 *
 * `ago()` answers "just now" for anything under ninety seconds, and seven call
 * sites wrote `${ago(x)} ago` — which read "closed just now ago" on a
 * commitment somebody had ticked a moment earlier (Vladimir, 2026-09-20).
 */
export function since(iso: string, now = Date.now()): string {
  const label = ago(iso, now);
  return label === 'just now' ? label : `${label} ago`;
}

export function ago(iso: string, now = Date.now()): string {
  const s = Math.max(0, (now - Date.parse(iso)) / 1000);
  if (s < 90) return 'just now';
  const m = s / 60;
  if (m < 90) return `${Math.round(m)} min`;
  const h = m / 60;
  if (h < 36) return `${Math.round(h)} hours`;
  const d = h / 24;
  if (d < 14) return `${Math.round(d)} days`;
  const w = d / 7;
  if (w < 8) return `${Math.round(w)} weeks`;
  return `${Math.round(d / 30)} months`;
}

/** "in 12 min", "in 3 hours", "tomorrow 09:00" — for what is ahead. */
export function until(iso: string, now = Date.now()): string {
  const s = (Date.parse(iso) - now) / 1000;
  if (s <= 0) return 'now';
  const m = s / 60;
  if (m < 60) return `in ${Math.max(1, Math.round(m))} min`;
  const h = m / 60;
  if (h < 24) return `in ${Math.round(h)} hours`;
  return `${dayLabel(iso).split(' · ')[0].toLowerCase()} ${clock(iso)}`;
}

export function groupByDay<T>(
  items: T[],
  at: (item: T) => string,
): Array<{ day: string; items: T[] }> {
  const groups = new Map<string, { day: string; items: T[] }>();
  for (const item of items) {
    const key = dayKey(at(item));
    const group = groups.get(key) ?? { day: at(item), items: [] };
    group.items.push(item);
    groups.set(key, group);
  }
  return [...groups.values()];
}

/** "Monday" — a day this week, said the way people say it. */
export const weekday = (iso: string): string =>
  new Intl.DateTimeFormat(LOCALE, { weekday: 'long' }).format(new Date(iso));

/** "Friday, 18 September" — the day as a page title. */
export const dayLong = (iso: string): string => {
  const d = new Date(iso);
  const weekday = new Intl.DateTimeFormat(LOCALE, { weekday: 'long' }).format(d);
  const rest = new Intl.DateTimeFormat(LOCALE, { day: 'numeric', month: 'long' }).format(d);
  return `${weekday}, ${rest}`;
};

/** "Wed 09:21" — where a commitment was said. */
export const weekdayClock = (iso: string): string =>
  `${new Intl.DateTimeFormat(LOCALE, { weekday: 'short' }).format(new Date(iso))} ${clock(iso)}`;

/** The wall clock at an offset into a meeting — "11:42:08". */
export const clockAt = (startIso: string, ms: number): string =>
  new Intl.DateTimeFormat(LOCALE, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(new Date(Date.parse(startIso) + ms));

export const isToday = (iso: string): boolean => dayKey(iso) === dayKey(new Date().toISOString());

export const isTomorrow = (iso: string): boolean =>
  dayKey(iso) === dayKey(new Date(Date.now() + 86_400_000).toISOString());

/**
 * A deadline as a person reads it (board 03, 2026-09-22).
 *
 * ⚠ Days, never hours. Nobody promises a thing at 14:30 in a meeting, and
 * "in 7 hours" for something due this evening reads as precision the product
 * does not have. The sign is what matters: late is its own word.
 */
export function dueLabel(iso: string, now = new Date()): string {
  const days = daysUntil(iso, now);
  if (days < -1) return `${-days} days late`;
  if (days === -1) return 'a day late';
  if (days === 0) return 'due today';
  if (days === 1) return 'due tomorrow';
  if (days <= 6) return `due ${new Date(iso).toLocaleDateString(undefined, { weekday: 'long' })}`;
  return `due ${dateLabel(iso)}`;
}

/** Whole days from today to that date — negative when it has passed. */
export function daysUntil(iso: string, now = new Date()): number {
  const midnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  return Math.round((midnight(new Date(iso)) - midnight(now)) / 86_400_000);
}

/** Midnight local on the day `days` from today, as an ISO instant. */
export function inDays(days: number, now = new Date()): string {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + days, 9, 0, 0, 0);
  return d.toISOString();
}

/** The next given weekday (1 = Monday … 5 = Friday), never today. */
export function nextWeekday(weekday: number, now = new Date()): string {
  const ahead = (weekday - now.getDay() + 7) % 7 || 7;
  return inDays(ahead, now);
}
